import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils/cn';

type Variant = 'solid' | 'outline' | 'ghost';
type Size = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

const base =
  'inline-flex items-center justify-center gap-2 font-sans font-semibold rounded-md ' +
  'transition-[background-color,border-color,color,box-shadow] duration-150 ' +
  'disabled:opacity-40 disabled:pointer-events-none cursor-pointer ' +
  'whitespace-nowrap';

const variants: Record<Variant, string> = {
  solid:
    'bg-brand text-white border border-brand hover:bg-brand-2 hover:border-brand-2 active:bg-brand-2',
  outline:
    'bg-bg-2 text-ink border border-line-2 hover:border-brand hover:text-brand active:bg-panel-2',
  ghost: 'bg-transparent text-muted border border-transparent hover:text-ink hover:bg-panel-2',
};

const sizes: Record<Size, string> = {
  sm: 'text-xs px-3 py-2',
  md: 'text-[13px] px-4 py-2.5',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'outline', size = 'md', className, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(base, variants[variant], sizes[size], className)}
      {...props}
    />
  );
});
